import { Flex, MenuButtonProps } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect } from 'react';
import {
  ISelectDropdownOption,
  SelectDropdown,
} from '~/components/SelectDropdown';
import { allowedSortDirections, allowedSortFields } from '~/searchApi';

import { sortOptions, sortSearchApiOptions } from './constants';

interface SortCreatorsDropDownProps extends MenuButtonProps {
  isShared?: boolean;
  sortField: allowedSortFields;
  sortDirection: allowedSortDirections;
  setSortingOrder: (
    field: allowedSortFields,
    direction: allowedSortDirections,
  ) => void;
  isNewUi?: boolean;
}

export const SortCreatorsDropDown: React.FC<SortCreatorsDropDownProps> = ({
  isShared = false,
  sortField,
  sortDirection,
  setSortingOrder,
  isNewUi = false,
  ...menuButtonProps
}) => {
  // NOTE/TODO:
  // all the props used in this component are provided by useFilterSortFields() hook.
  // So, if we manage to make the above hook as a singleton (by using zustand or react context),
  // then we can remove all the props from this component and use the hook directly here.
  // This will make the code more readable and maintainable.

  const defaultValue = `${sortField}:${sortDirection}`;

  const router = useRouter();
  const { sortBy } = router.query as { sortBy: string };

  useEffect(() => {
    if (!sortBy) return;
    const [field, directionOrPlatform] = sortBy.split(':');

    setSortingOrder(
      field as allowedSortFields,
      directionOrPlatform as allowedSortDirections,
    );
  }, [sortBy, setSortingOrder]);

  const onValChange = useCallback(
    (value: string) => {
      router.replace({
        query: {
          ...router.query,
          sortBy: value,
        },
      });
    },
    [router],
  );

  const getSelectDropDownOptions: (
    excludedValues: string[],
  ) => ISelectDropdownOption[] = (excludedValues) => {
    return (!isShared ? sortSearchApiOptions : sortOptions)
      .filter(({ value }) => !excludedValues.includes(value))
      .map(({ friendlyValue, icon: Icon, trackId, ...rest }) => ({
        ...rest,
        friendlyValue,
        render: (isSelected) => (
          <Flex
            alignItems="center"
            data-testid="dropdown-option"
            track-id={trackId}
            fontWeight={isSelected ? 500 : 'normal'}
          >
            <Icon marginRight="2" boxSize={4} />
            {friendlyValue}
          </Flex>
        ),
      }));
  };

  const options = getSelectDropDownOptions([]);

  return (
    <SelectDropdown
      menuButtonProps={{
        fontSize: { md: 'md' },
        paddingX: '4',
        height: '10',
        ...(isNewUi
          ? {
              ...menuButtonProps,
              fontWeight: 500,
              border: 'none',
              background: 'primary.50',
              _hover: {
                border: 'none',
                backgroundColor: 'primary.100',
                outlineColor: 'transparent',
              },
              _focusVisible: {
                borderColor: 'none',
                outlineColor: 'primary.100',
                outlineWidth: 4,
                outlineStyle: 'solid',
              },
              _expanded: {
                border: 'none',
                backgroundColor: 'primary.200',
                outlineColor: 'transparent',
              },
              _active: {
                border: 'none',
                backgroundColor: 'primary.200',
                outlineColor: 'transparent',
              },
            }
          : {}),
        ...menuButtonProps,
      }}
      menuListProps={{
        paddingY: '1',
        marginY: '1',
      }}
      menuItemOptionProps={{
        paddingY: '1',
        width: 60,
      }}
      defaultValue={sortBy || defaultValue}
      itemPrefix="Sort by"
      onChange={onValChange}
      options={options}
      selectedPrefix={isNewUi ? 'Sort by: ' : 'Sort by'}
      trackId="foam-myagency-stack-sortbybutton"
    />
  );
};
